/**
 * HARC 予測データ更新の目安時刻（JST）。
 *
 * HARC 側の公称更新時刻は 08:30 JST だが、運用上前後することがあるため、
 * 余裕を持たせて 09:30 JST を境界とする。運用観察に応じて微調整する。
 */
export const HARC_UPDATE_BOUNDARY_JST = { hour: 9, minute: 30 } as const;

/**
 * HARC キャッシュの最大寿命（ミリ秒）。
 *
 * TanStack Query の staleTime / gcTime、および persister の maxAge すべての上限として機能し、
 * 境界時刻の計算が何らかの理由で狂った場合の最終セーフティ。
 */
export const HARC_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

function isBeforeBoundary(jstHour: number, jstMinute: number): boolean {
  return (
    jstHour < HARC_UPDATE_BOUNDARY_JST.hour ||
    (jstHour === HARC_UPDATE_BOUNDARY_JST.hour && jstMinute < HARC_UPDATE_BOUNDARY_JST.minute)
  );
}

/**
 * 指定時刻において「HARC が提供している最新の予測データ日付」を 'YYYY-MM-DD' 形式で返す。
 *
 * 境界時刻（{@link HARC_UPDATE_BOUNDARY_JST}）以降は当日の日付、
 * 境界時刻より前は前日の日付を返す（JST 基準）。
 *
 * @param now - 判定に用いる時刻。省略時は現在時刻。
 * @returns HARC データ日付の 'YYYY-MM-DD' 文字列（JST 基準）。
 */
export function getHarcDataDate(now: Date = new Date()): string {
  const jst = new Date(now.getTime() + JST_OFFSET_MS);
  const year = jst.getUTCFullYear();
  const month = jst.getUTCMonth();
  const day = jst.getUTCDate();
  const hour = jst.getUTCHours();
  const minute = jst.getUTCMinutes();

  const offsetDays = isBeforeBoundary(hour, minute) ? -1 : 0;
  const effective = new Date(Date.UTC(year, month, day + offsetDays));
  const yyyy = effective.getUTCFullYear();
  const mm = String(effective.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(effective.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * 次の HARC データ更新境界時刻までのミリ秒を返す。
 *
 * TanStack Query の staleTime 計算に使用する。境界時刻ちょうどに now が一致した場合は
 * 翌日の境界までを返す（= 24h）。
 *
 * @param now - 基準となる時刻。省略時は現在時刻。
 * @returns 次の境界時刻までのミリ秒（常に正の値）。
 */
export function msUntilNextHarcBoundary(now: Date = new Date()): number {
  const jst = new Date(now.getTime() + JST_OFFSET_MS);
  const year = jst.getUTCFullYear();
  const month = jst.getUTCMonth();
  const day = jst.getUTCDate();
  const hour = jst.getUTCHours();
  const minute = jst.getUTCMinutes();

  const offsetDays = isBeforeBoundary(hour, minute) ? 0 : 1;
  // JST 壁時計時刻を UTC のふりをしたタイムスタンプで表現し、そこから 9h 戻して実 UTC に変換
  const boundaryJstWallMs = Date.UTC(
    year,
    month,
    day + offsetDays,
    HARC_UPDATE_BOUNDARY_JST.hour,
    HARC_UPDATE_BOUNDARY_JST.minute
  );
  const boundaryRealMs = boundaryJstWallMs - JST_OFFSET_MS;
  return boundaryRealMs - now.getTime();
}
